import type { Delta } from "@wbim/quill-editor"

import { type EmdEsgSectorGroupKey } from "../services/sectors"

import { emdClient } from "@/utils/apiClients"
import BlobApi from "@/apis/blobApi"

export interface IssuerSectorInfo {
  issuerId: string
  emdSector: string
}

export interface FactorMateriality {
  code: string // factor code
  susImpact: number
  busImpact: number
}

export interface IssueScore {
  code: string // issue code
  score: number
  comments: string
}

export interface ScoreSummary {
  score: number
  environmental: number
  social: number
  governance: number
}

export const sentimentList = ["negative", "stable", "positive"] as const
export type SentimentValue = (typeof sentimentList)[number]

export type IncidentOutlookResult = {
  incidentSentiment: SentimentValue
  incidentComment: Delta
  outlookSentiment: SentimentValue
  outlookComment: Delta
}

export class CorpEsgApi {
  private pathRoot = "corp-esg"

  private readonly blobApi: BlobApi
  constructor() {
    this.blobApi = new BlobApi()
  }

  private makeGetUrl(path: string) {
    return `${this.pathRoot}/${path}`
  }

  private makePostUrl(path: string) {
    return `${this.pathRoot}/${path}`
  }

  private getApiClient() {
    if (!emdClient) throw new Error("emdClient is not initialized")
    return emdClient!
  }

  getFactorMaterialities(sector: EmdEsgSectorGroupKey) {
    const url = this.makeGetUrl(`factor-materialities/${sector}.json`)
    return this.blobApi.getBlobData<FactorMateriality[]>(url)
  }

  async saveFactorMaterialities(sector: EmdEsgSectorGroupKey, materialities: FactorMateriality[]) {
    const url = this.makePostUrl(`factor-materialities/${sector}.json`)
    await this.blobApi.writeBlobData(url, materialities)
    return true
  }

  async getIssuerSectorInfo(issuerId: string) {
    const url = "/api/corp_issuers/" + issuerId
    const api = this.getApiClient()
    const { status, data } = await api.get(url)
    if (status === 200) {
      const { issuer_id, emd_sector } = data
      return <IssuerSectorInfo>{
        issuerId: String(issuer_id),
        emdSector: emd_sector
      }
    } else {
      throw new Error("can not load issuer info")
    }
  }

  async getIssuerScores(issuerId: string) {
    const url = this.makeGetUrl(`issuer-scores/${issuerId}.json`)
    try {
      return await this.blobApi.getBlobData<IssueScore[]>(url)
    } catch {
      return []
    }
  }

  async saveIssuerScores(issuerId: string, scores: IssueScore[]) {
    const url = this.makePostUrl(`issuer-scores/${issuerId}.json`)
    await this.blobApi.writeBlobData(url, scores)
    return
  }

  async getIncidentOutlook(issuerId: string) {
    const url = this.makeGetUrl(`issuer-incident-outlook/${issuerId}.json`)
    try {
      return this.blobApi.getBlobData<IncidentOutlookResult | null>(url)
    } catch {
      return null
    }
  }

  async saveIncidentOutlook(issuerId: string, result: IncidentOutlookResult) {
    const url = this.makePostUrl(`issuer-incident-outlook/${issuerId}.json`)
    await this.blobApi.writeBlobData(url, result)
  }

  async saveEsgSummaryToLegacyApi(issuerId: string, scoreSummary: ScoreSummary) {
    const api = this.getApiClient()

    const { status } = await api.patch(`/api/corp_crm_models/${issuerId}`, {
      path: "esg",
      value: scoreSummary,
      update_esg_date: true
    })

    if (status !== 200) {
      throw new Error("failed to patch changed to api")
    }
  }

  async getEsgSummaryFromLegacy(issuerId: string) {
    const api = this.getApiClient()
    const { data, status } = await api.get(`/api/corp_crm_models/${issuerId}?path=esg`)
    if (status !== 200) {
      throw new Error("failed to get esg summary")
    }
    return data as ScoreSummary
  }
}
